import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS, UserRole, ROLE_PERMISSIONS, UserProfile } from '../../models/authTypes';
import { collection, getDocs, doc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { FaChevronDown, FaChevronUp, FaInfoCircle } from 'react-icons/fa';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: #1a1a2e;
  margin-bottom: 2rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  margin-bottom: 2rem;
  overflow: hidden;
`;

const DocumentationSection = styled.div`
  margin-top: 2rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 1.5rem;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  margin-bottom: 1rem;
  
  h3 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  svg {
    margin-left: auto;
  }
`;

interface SectionContentProps {
  $isOpen: boolean;
}

const SectionContent = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$isOpen',
})<SectionContentProps>`
  max-height: ${({ $isOpen }) => ($isOpen ? '5000px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
  background: #f8fafc;
  border-radius: 8px;
  margin-top: ${({ $isOpen }) => ($isOpen ? '1rem' : '0')};
  
  > div {
    padding: ${({ $isOpen }) => ($isOpen ? '1.5rem' : '0 1.5rem')};
  }
`;

const InfoBox = styled.div`
  background: #e3f2fd;
  border-left: 4px solid #1976d2;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 0 4px 4px 0;
  
  h4 {
    margin-top: 0;
    color: #0d47a1;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const PermissionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
`;

const PermissionCategory = styled.div`
  h4 {
    color: #2d3748;
    margin-bottom: 0.75rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e2e8f0;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      padding: 0.35rem 0;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      
      &::before {
        content: '•';
        color: #4a5568;
      }
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  
  th, td {
    padding: 0.75rem 1rem;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }
  
  th {
    background-color: #f8fafc;
    font-weight: 600;
    color: #4a5568;
  }
  
  tr:hover {
    background-color: #f8fafc;
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  cursor: pointer;
  width: 18px;
  height: 18px;
`;

const RoleBadge = styled.span<{ role: UserRole }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  ${({ role }) => {
    switch (role) {
      case 'superadmin':
        return 'background-color: #805ad5; color: white;';
      case 'admin':
        return 'background-color: #3182ce; color: white;';
      case 'comisario':
        return 'background-color: #38a169; color: white;';
      default:
        return 'background-color: #718096; color: white;';
    }
  }}
`;

interface User extends UserProfile {}

interface RolePermissionsState {
  [role: string]: string[];
}

const PermissionsManager: React.FC = () => {
  const { currentUser } = useAuth();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [customPermissions, setCustomPermissions] = useState<Record<string, string[]>>({});
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [editingRole, setEditingRole] = useState<UserRole | null>(null);
  const [rolePermissions, setRolePermissions] = useState<RolePermissionsState>(ROLE_PERMISSIONS);
  const [showRolePermissions, setShowRolePermissions] = useState<Record<UserRole, boolean>>({
    superadmin: false,
    admin: false,
    comisario: false,
    usuario: false,
  });

  // Mapeo de permisos a etiquetas legibles
  const PERMISSION_LABELS: Record<string, string> = {
    'view:leagues': 'Ver Ligas',
    'view:races': 'Ver Carreras',
    'view:standings': 'Ver Clasificaciones',
    'view:lastres': 'Ver Lastres',
    'view:teams': 'Ver Equipos',
    'view:drivers': 'Ver Pilotos',
    'view:penalties': 'Ver Sanciones',
    'view:penalty_rules': 'Ver Reglas Automáticas',
    'view:claims': 'Ver Reclamaciones',
    'view:appeals': 'Ver Apelaciones',
    'view:events': 'Ver Eventos',
    'view:points': 'Ver Puntuaciones',
    'view:guide': 'Ver Guía',
    'view:ia_redaccion_circuitos': 'Ver Redacción Circuitos (IA)',
    'view:warmup': 'Ver Warmup',
    'view:blog': 'Ver Blog',
    'manage:leagues': 'Gestionar Ligas',
    'manage:races': 'Gestionar Carreras',
    'manage:teams': 'Gestionar Equipos',
    'manage:drivers': 'Gestionar Pilotos',
    'manage:penalties': 'Gestionar Sanciones',
    'manage:penalty_rules': 'Gestionar Reglas',
    'manage:claims': 'Gestionar Reclamaciones',
    'manage:appeals': 'Gestionar Apelaciones',
    'manage:events': 'Gestionar Eventos',
    'manage:points': 'Gestionar Puntuaciones',
    'admin:users': 'Administrar Usuarios',
    'admin:permissions': 'Administrar Permisos',
  };

  // Cargar usuarios y permisos desde Firestore
  useEffect(() => {
    const loadData = async () => {
      try {
        // Cargar usuarios
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs.map(doc => ({
          ...doc.data() as Omit<UserProfile, 'uid'>,
          uid: doc.id,
        }));
        setUsers(usersData);
        
        // Cargar permisos de roles
        const rolesDoc = await getDocs(collection(db, 'system'));
        if (!rolesDoc.empty) {
          const rolesData = rolesDoc.docs[0].data();
          setRolePermissions(prev => ({
            ...prev,
            ...rolesData
          }));
        } else {
          // Si no hay roles en la base de datos, usar los permisos por defecto
          setRolePermissions(ROLE_PERMISSIONS);
          
          // Guardar los permisos por defecto en la base de datos
          const rolesRef = doc(db, 'system', 'roles');
          await setDoc(rolesRef, ROLE_PERMISSIONS, { merge: true });
        }
      } catch (error) {
        console.error('Error al cargar datos:', error);
        // En caso de error, usar los permisos por defecto
        setRolePermissions(ROLE_PERMISSIONS);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Verificar si un usuario tiene un permiso específico
  const hasPermission = (user: UserProfile, perm: string) => {
    if (user.role === 'superadmin') return true;
    return user.permissions.includes(perm);
  };
  
  // Verificar si un permiso es parte de los permisos predeterminados del rol
  const isDefaultPermission = (user: UserProfile, permission: string) => {
    return rolePermissions[user.role as UserRole]?.includes(permission) || false;
  };

  // Actualizar el rol de un usuario
  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      setSaving(true);
      const userDoc = doc(db, 'users', userId);
      const permissionsForRole = rolePermissions[newRole] || [];
      
      // Mantener los permisos personalizados que no entren en conflicto con el nuevo rol
      const existingCustomPermissions = customPermissions[userId] || [];
      const newCustomPermissions = existingCustomPermissions.filter(
        perm => !permissionsForRole.includes(perm)
      );
      
      await updateDoc(userDoc, {
        role: newRole,
        permissions: [...permissionsForRole, ...newCustomPermissions],
      });
      
      setUsers(users.map(user => 
        user.uid === userId 
          ? { ...user, role: newRole, permissions: [...permissionsForRole, ...newCustomPermissions] }
          : user
      ));
      
      setSaveMessage({ type: 'success', message: 'Rol actualizado correctamente' });
    } catch (error) {
      console.error('Error updating role:', error);
      setSaveMessage({ type: 'error', message: 'Error al actualizar el rol' });
    } finally {
      setSaving(false);
    }
  };

  // Guardar los permisos de los roles en Firebase
  const saveRolePermissions = async () => {
    try {
      setSaving(true);
      
      // Actualizar los permisos en la base de datos
      const rolesRef = doc(db, 'system', 'roles');
      await updateDoc(rolesRef, rolePermissions);
      
      // Actualizar los permisos de todos los usuarios afectados
      const batchUpdates = users.map(async (user) => {
        const userDoc = doc(db, 'users', user.uid);
        const rolePerms = rolePermissions[user.role as UserRole] || [];
        const customPerms = customPermissions[user.uid] || [];
        
        // Filtrar permisos personalizados que no estén incluidos en los permisos del rol
        const filteredCustomPerms = customPerms.filter(perm => !rolePerms.includes(perm));
        
        // Combinar permisos del rol con permisos personalizados (sin duplicados)
        const allPermissions = [...rolePerms, ...filteredCustomPerms];
        const uniquePermissions = allPermissions.filter((perm, index) => allPermissions.indexOf(perm) === index);
        
        // Actualizar el documento del usuario
        await updateDoc(userDoc, {
          permissions: uniquePermissions
        });
        
        // Devolver el usuario actualizado
        return {
          ...user,
          permissions: uniquePermissions
        };
      });
      
      // Esperar a que todas las actualizaciones se completen
      const updatedUsers = await Promise.all(batchUpdates);
      
      // Actualizar el estado local
      setUsers(updatedUsers);
      
      setSaveMessage({ type: 'success', message: 'Permisos guardados correctamente' });
    } catch (error) {
      console.error('Error al guardar los permisos:', error);
      setSaveMessage({ type: 'error', message: 'Error al guardar los permisos' });
    } finally {
      setSaving(false);
    }
  };

  // Actualizar un permiso específico
  const togglePermission = async (userId: string, permission: string) => {
    try {
      setSaving(true);
      const user = users.find(u => u.uid === userId);
      if (!user) return;

      const userDoc = doc(db, 'users', userId);
      const currentPermissions = user.permissions || [];
      const isDefault = rolePermissions[user.role as UserRole]?.includes(permission) || false;
      
      let newPermissions: string[];
      
      if (currentPermissions.includes(permission)) {
        // Si el permiso ya está asignado, lo quitamos
        newPermissions = currentPermissions.filter(p => p !== permission);
      } else {
        // Si el permiso no está asignado, lo agregamos
        newPermissions = [...currentPermissions, permission];
      }
      
      await updateDoc(userDoc, { permissions: newPermissions });
      
      setUsers(users.map(u => 
        u.uid === userId ? { ...u, permissions: newPermissions } : u
      ));
      
      // Actualizamos los permisos personalizados
      setCustomPermissions(prev => ({
        ...prev,
        [userId]: newPermissions.filter(p => !rolePermissions[user.role as UserRole]?.includes(p))
      }));
      
      setSaveMessage({ type: 'success', message: 'Permiso actualizado correctamente' });
    } catch (error) {
      console.error('Error updating permission:', error);
      setSaveMessage({ type: 'error', message: 'Error al actualizar el permiso' });
    } finally {
      setSaving(false);
    }
  };



  const toggleRoleSection = (role: UserRole) => {
    setShowRolePermissions(prev => ({
      ...prev,
      [role]: !prev[role]
    }));
  };

  // Alternar un permiso para un rol específico
  const toggleRolePermission = (role: UserRole, permission: string) => {
    setRolePermissions(prev => {
      const currentPermissions = [...(prev[role] || [])];
      const permissionIndex = currentPermissions.indexOf(permission);
      
      if (permissionIndex === -1) {
        // Agregar el permiso si no existe
        return {
          ...prev,
          [role]: [...currentPermissions, permission]
        };
      } else {
        // Eliminar el permiso si ya existe
        const updatedPermissions = [...currentPermissions];
        updatedPermissions.splice(permissionIndex, 1);
        return {
          ...prev,
          [role]: updatedPermissions
        };
      }
    });
  };

  // Obtener todos los permisos únicos para mostrarlos en la tabla
  // Mapeo de permisos a español
  const PERMISSIONS_TRANSLATIONS: Record<string, string> = {
    // Visualización
    'view:leagues': 'Ver Ligas',
    'view:races': 'Ver Carreras',
    'view:standings': 'Ver Clasificaciones',
    'view:lastres': 'Ver Lastres',
    'view:teams': 'Ver Equipos',
    'view:drivers': 'Ver Pilotos',
    'view:penalties': 'Ver Sanciones',
    'view:penalty_rules': 'Ver Reglas Automáticas',
    'view:claims': 'Ver Reclamaciones',
    'view:appeals': 'Ver Apelaciones',
    'view:events': 'Ver Eventos',
    'view:points': 'Ver Puntuaciones',
    'view:guide': 'Ver Guía',
    'view:ia_redaccion_circuitos': 'Ver Redacción Circuitos (IA)',
    'view:warmup': 'Ver Warmup',
    'view:blog': 'Ver Blog',
    'view:thumbnails_generator': 'Ver Generador de Miniaturas',
    // Gestión
    'manage:leagues': 'Gestionar Ligas',
    'manage:races': 'Gestionar Carreras',
    'manage:teams': 'Gestionar Equipos',
    'manage:drivers': 'Gestionar Pilotos',
    'manage:penalties': 'Gestionar Sanciones',
    'manage:penalty_rules': 'Gestionar Reglas',
    'manage:claims': 'Gestionar Reclamaciones',
    'manage:appeals': 'Gestionar Apelaciones',
    'manage:events': 'Gestionar Eventos',
    'manage:points': 'Gestionar Puntuaciones',
    
    // Administración
    'admin:users': 'Administrar Usuarios',
    'admin:permissions': 'Administrar Permisos'
  };

  // Agrupar permisos por categoría para mejor visualización
  type Permission = string;
  type PermissionGroup = Record<string, Permission[]>;
  
  const PERMISSIONS_GROUPS: PermissionGroup = {
    'Visualización': [
      'view:leagues', 'view:races', 'view:standings', 'view:lastres',
      'view:teams', 'view:drivers', 'view:penalties', 'view:penalty_rules',
      'view:claims', 'view:appeals', 'view:events', 'view:points', 'view:guide',
      'view:ia_redaccion_circuitos', 'view:warmup', 'view:thumbnails_generator', 'view:blog'
    ],
    'Gestión': [
      'manage:leagues', 'manage:races', 'manage:teams', 'manage:drivers',
      'manage:penalties', 'manage:penalty_rules', 'manage:claims',
      'manage:appeals', 'manage:events', 'manage:points'
    ],
    'Administración': [
      'admin:users', 'admin:permissions'
    ]
  };

  // Obtener todos los permisos en el orden correcto según los grupos
  const allPermissions = Object.values(PERMISSIONS_GROUPS).flat();
  const roles: UserRole[] = ['superadmin', 'admin', 'comisario', 'usuario'];

  if (loading) {
    return <div>Cargando usuarios...</div>;
  }

  // Solo permitir acceso a superadmin
  if (currentUser?.role !== 'superadmin') {
    return (
      <Container>
        <Card>
          <h2>Acceso denegado</h2>
          <p>No tienes permiso para acceder a esta página.</p>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Administrador de Permisos</Title>
      
      <Card style={{ 
        backgroundColor: '#f8f9fa', 
        borderLeft: '4px solid #4a6cf7',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ marginTop: 0, color: '#2d3748' }}>¿Cómo gestionar los permisos?</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <strong>1. Configurar Permisos por Rol:</strong>
            <ul style={{ margin: '0.5rem 0 0 1.5rem' }}>
              <li>Haz clic en un rol para expandir/contraer sus permisos</li>
              <li>Marca/desmarca los checkboxes para agregar o quitar permisos</li>
              <li>Haz clic en "Guardar Cambios en Roles" para aplicar los cambios</li>
            </ul>
          </div>
          <div>
            <strong>2. Personalización por Usuario:</strong>
            <ul style={{ margin: '0.5rem 0 0 1.5rem' }}>
              <li>Los permisos que establezcas en cada rol se aplicarán a todos los usuarios con ese rol</li>
              <li>Puedes personalizar permisos individuales en la sección "Permisos por Usuario" más abajo</li>
            </ul>
          </div>
        </div>
      </Card>
      
      {saveMessage && (
        <div style={{
          padding: '1rem',
          marginBottom: '1.5rem',
          borderRadius: '0.5rem',
          backgroundColor: saveMessage.type === 'success' ? '#e6fffa' : '#fff5f5',
          color: saveMessage.type === 'success' ? '#234e52' : '#9b2c2c',
          border: `1px solid ${saveMessage.type === 'success' ? '#81e6d9' : '#fc8181'}`,
        }}>
          {saveMessage.message}
        </div>
      )}

      <Card style={{ marginBottom: '2rem' }}>
        <h2>Configuración de Perfiles Predeterminados</h2>
        <p style={{ marginBottom: '1.5rem' }}>
          Establece los permisos predeterminados para cada rol. Estos permisos se aplicarán automáticamente a los usuarios con cada rol.
        </p>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {roles.map(role => (
            <div key={role} style={{ 
              border: '1px solid #e2e8f0', 
              borderRadius: '0.5rem',
              overflow: 'hidden'
            }}>
              <div 
                onClick={() => toggleRoleSection(role)}
                style={{
                  backgroundColor: '#f7fafc',
                  padding: '0.75rem 1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderBottom: showRolePermissions[role] ? '1px solid #e2e8f0' : 'none'
                }}
              >
                <h3 style={{ 
                  margin: 0, 
                  textTransform: 'capitalize',
                  color: '#2d3748',
                  fontSize: '1.1rem'
                }}>
                  {role === 'comisario' ? 'Comisario' : 
                   role === 'superadmin' ? 'Super Administrador' : 
                   role.charAt(0).toUpperCase() + role.slice(1)}
                </h3>
                <span style={{ fontSize: '0.9rem', color: '#4a5568' }}>
                  {showRolePermissions[role] ? 'Ocultar permisos' : 'Mostrar permisos'}
                </span>
              </div>
              
              {showRolePermissions[role] && (
                <div style={{ padding: '1rem', backgroundColor: 'white' }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                    gap: '0.75rem',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    padding: '0.5rem'
                  }}>
                    {allPermissions.map(permission => (
                      <label 
                        key={permission} 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.5rem',
                          borderRadius: '0.25rem',
                          backgroundColor: rolePermissions[role]?.includes(permission) ? '#ebf8ff' : 'transparent',
                          transition: 'background-color 0.2s',
                          cursor: 'pointer'
                        }}
                        onMouseEnter={(e) => {
                          const target = e.currentTarget;
                          target.style.backgroundColor = rolePermissions[role]?.includes(permission) ? '#bee3f8' : '#f7fafc';
                        }}
                        onMouseLeave={(e) => {
                          const target = e.currentTarget;
                          target.style.backgroundColor = rolePermissions[role]?.includes(permission) ? '#ebf8ff' : 'transparent';
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={rolePermissions[role]?.includes(permission) || false}
                          onChange={() => toggleRolePermission(role, permission)}
                          style={{
                            marginRight: '0.5rem',
                            cursor: 'pointer'
                          }}
                        />
                        <span style={{ fontSize: '0.9rem' }}>{PERMISSIONS_TRANSLATIONS[permission] || permission}</span>
                      </label>
                    ))}
                  </div>
                  
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    marginTop: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #edf2f7'
                  }}>
                    <button
                      onClick={() => {
                        // Aquí iría la lógica para guardar los cambios en la base de datos
                        saveRolePermissions();
                      }}
                      style={{
                        backgroundColor: '#4299e1',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '0.25rem',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => {
                        if (!saving) {
                          e.currentTarget.style.backgroundColor = '#3182ce';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!saving) {
                          e.currentTarget.style.backgroundColor = '#4299e1';
                        } else {
                          e.currentTarget.style.backgroundColor = '#a0aec0';
                        }
                      }}
                      onMouseDown={(e) => {
                        if (!saving) {
                          e.currentTarget.style.backgroundColor = '#2c5282';
                        }
                      }}
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          Guardando...
                        </>
                      ) : (
                        'Guardar cambios'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
      
      <Card>
        <h2>Usuarios</h2>
        <Table>
          <thead>
            <tr>
              <th>Usuario</th>
              <th>Rol</th>
              <th>Correo</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.uid}>
                <td>{user.displayName || 'Sin nombre'}</td>
                <td>
                  <RoleBadge role={user.role}>
                    {user.role}
                  </RoleBadge>
                </td>
                <td>{user.email}</td>
                <td>
                  <select
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.uid, e.target.value as UserRole)}
                    disabled={saving || user.uid === currentUser.uid}
                  >
                    <option value="usuario">Usuario</option>
                    <option value="comisario">Comisario</option>
                    <option value="admin">Administrador</option>
                    <option value="superadmin">Super Admin</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
      
      <Card>
        <h2>Permisos por Usuario</h2>
        {Object.entries(PERMISSIONS_GROUPS).map(([group, groupPermissions]: [string, string[]]) => (
          <div key={group} style={{ marginBottom: '3rem' }}>
            <h3 style={{ margin: '1rem 0', color: '#2d3748' }}>{group}</h3>
            <div style={{ 
              width: '100%',
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              border: '1px solid #e2e8f0',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <Table style={{ minWidth: 'max-content', width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'white' }}>
                      Usuario
                    </th>
                    {groupPermissions.map((permission: string) => (
                      <th key={permission} style={{ minWidth: '120px' }}>
                        {PERMISSION_LABELS[permission] || permission}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.uid}>
                      <td style={{ whiteSpace: 'nowrap', position: 'sticky', left: 0, background: 'white' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span>{user.displayName || user.email}</span>
                          <RoleBadge role={user.role as UserRole}>
                            {user.role}
                          </RoleBadge>
                        </div>
                      </td>
                      {groupPermissions.map((perm) => {
                        const hasPerm = hasPermission(user, perm);
                        const isDefault = isDefaultPermission(user, perm);
                        
                        return (
                          <td key={perm} style={{ textAlign: 'center' }}>
                            <Checkbox
                              checked={hasPerm}
                              onChange={(e) => {
                                // Solo permitir cambios si el usuario actual es superadmin
                                // o si está modificando un permiso que no es de superadmin
                                if (currentUser?.role === 'superadmin' || 
                                    (currentUser?.role === 'admin' && !perm.startsWith('admin:'))) {
                                  togglePermission(user.uid, perm);
                                }
                              }}
                              disabled={
                                // Deshabilitar si el usuario actual no tiene permisos para modificar
                                // o si el permiso es de superadmin y el usuario actual no es superadmin
                                saving ||
                                (user.role === 'superadmin' && currentUser?.role !== 'superadmin') ||
                                (perm.startsWith('admin:') && currentUser?.role !== 'superadmin')
                              }
                              style={{
                                cursor: isDefault ? 'not-allowed' : 'pointer',
                                opacity: isDefault ? 0.7 : 1,
                              }}
                              title={isDefault ? 'Permiso predeterminado del rol' : ''}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
              </div>
            </div>
          ))}
      </Card>
      
      <Card>
        <h2>Descripción de Roles</h2>
        <ul>
          <li>
            <strong>Super Admin</strong>: Acceso total al sistema, incluyendo la gestión de permisos.
          </li>
          <li>
            <strong>Administrador</strong>: Acceso a todas las funcionalidades excepto la gestión de permisos.
          </li>
          <li>
            <strong>Comisario</strong>: Acceso a gestión de sanciones, reclamaciones y apelaciones.
          </li>
          <li>
            <strong>Usuario</strong>: Acceso básico a la visualización de carreras y clasificaciones.
          </li>
        </ul>
        
        <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>
          <h3>Instrucciones:</h3>
          <ul style={{ marginTop: '0.5rem' }}>
            <li>Los permisos con fondo verde claro son los predeterminados del rol.</li>
            <li>Puedes agregar permisos adicionales a cualquier usuario.</li>
            <li>Para quitar un permiso, primero debes cambiar el rol a uno que no lo incluya por defecto.</li>
            <li>Los superadministradores tienen todos los permisos y no pueden ser modificados.</li>
          </ul>
        </div>

        <DocumentationSection>
          <SectionHeader onClick={() => setShowDocumentation(!showDocumentation)}>
            <h3><FaInfoCircle /> Documentación Completa de Roles y Permisos</h3>
            {showDocumentation ? <FaChevronUp /> : <FaChevronDown />}
          </SectionHeader>
          <SectionContent $isOpen={showDocumentation}>
            <div>
              <h3>Guía de Administración de Roles y Permisos</h3>
              <p>El Administrador de Permisos es una herramienta poderosa que permite gestionar los niveles de acceso de los usuarios en la plataforma. Esta página está diseñada específicamente para administradores del sistema y proporciona un control detallado sobre lo que cada tipo de usuario puede ver y hacer.</p>
              
              <InfoBox>
                <h4><FaInfoCircle /> Acceso</h4>
                <p><strong>Accesible solo para:</strong> Super Administradores</p>
                <p><strong>Ubicación:</strong> Menú de administración &gt; Roles y Permisos</p>
                <p><strong>Requisitos:</strong> Debes tener el rol de superadmin para acceder a esta sección</p>
              </InfoBox>

              <h4>Secciones Principales</h4>
              
              <h5>1. Lista de Usuarios</h5>
              <p>En la parte superior de la página, encontrarás una tabla con todos los usuarios registrados en la plataforma. Para cada usuario se muestra:</p>
              <ul>
                <li><strong>Nombre:</strong> Nombre mostrado del usuario</li>
                <li><strong>Rol:</strong> Rol actual del usuario</li>
                <li><strong>Correo electrónico:</strong> Dirección de correo del usuario</li>
                <li><strong>Acciones:</strong> Menú desplegable para cambiar el rol del usuario</li>
              </ul>

              <h5>2. Gestión de Roles</h5>
              <p>Los roles definen conjuntos predeterminados de permisos. Los roles disponibles son:</p>
              
              <PermissionGrid>
                <PermissionCategory>
                  <h4>Usuario (usuario)</h4>
                  <ul>
                    <li>Acceso básico a la plataforma</li>
                    <li>Puede ver contenido público y participar en eventos</li>
                  </ul>
                </PermissionCategory>

                <PermissionCategory>
                  <h4>Comisario (comisario)</h4>
                  <ul>
                    <li>Permisos extendidos para gestionar carreras</li>
                    <li>Puede registrar resultados y aplicar sanciones básicas</li>
                  </ul>
                </PermissionCategory>

                <PermissionCategory>
                  <h4>Administrador (admin)</h4>
                  <ul>
                    <li>Acceso completo a la gestión de la plataforma</li>
                    <li>No puede modificar configuraciones críticas del sistema</li>
                  </ul>
                </PermissionCategory>

                <PermissionCategory>
                  <h4>Super Administrador (superadmin)</h4>
                  <ul>
                    <li>Control total sobre la plataforma</li>
                    <li>Único que puede gestionar roles y permisos de otros usuarios</li>
                  </ul>
                </PermissionCategory>
              </PermissionGrid>

              <h5>3. Matriz de Permisos</h5>
              <p>La sección principal muestra una matriz detallada de permisos organizados en categorías:</p>

              <PermissionGrid>
                <PermissionCategory>
                  <h4>a) Visualización</h4>
                  <ul>
                    <li>Ver Ligas</li>
                    <li>Ver Carreras</li>
                    <li>Ver Clasificaciones</li>
                    <li>Ver Lastres</li>
                    <li>Ver Equipos</li>
                    <li>Ver Pilotos</li>
                    <li>Ver Sanciones</li>
                    <li>Ver Reglas Automáticas</li>
                    <li>Ver Reclamaciones</li>
                    <li>Ver Apelaciones</li>
                    <li>Ver Eventos</li>
                    <li>Ver Puntuaciones</li>
                    <li>Ver Guía</li>
                  </ul>
                </PermissionCategory>

                <PermissionCategory>
                  <h4>b) Gestión</h4>
                  <ul>
                    <li>Gestionar Ligas</li>
                    <li>Gestionar Carreras</li>
                    <li>Gestionar Equipos</li>
                    <li>Gestionar Pilotos</li>
                    <li>Gestionar Sanciones</li>
                    <li>Gestionar Reglas</li>
                    <li>Gestionar Reclamaciones</li>
                    <li>Gestionar Apelaciones</li>
                    <li>Gestionar Eventos</li>
                    <li>Gestionar Puntuaciones</li>
                  </ul>
                </PermissionCategory>

                <PermissionCategory>
                  <h4>c) Administración</h4>
                  <ul>
                    <li>Administrar Usuarios</li>
                    <li>Administrar Permisos (solo superadmin)</li>
                  </ul>
                </PermissionCategory>
              </PermissionGrid>

              <h4>Funcionalidades Clave</h4>
              
              <h5>Cambiar el Rol de un Usuario</h5>
              <ol>
                <li>Localiza al usuario en la tabla de usuarios</li>
                <li>En la columna "Rol", selecciona el nuevo rol del menú desplegable</li>
                <li>El sistema actualizará automáticamente los permisos del usuario según el rol seleccionado</li>
              </ol>

              <h5>Modificar Permisos Individuales</h5>
              <ol>
                <li>Navega a la sección "Permisos por Usuario"</li>
                <li>Localiza la fila correspondiente al usuario</li>
                <li>Marca/desmarca los checkboxes según los permisos que desees otorgar/revocar</li>
                <li>Los checkboxes atenuados indican permisos predeterminados del rol</li>
                <li>Los checkboxes normales indican permisos personalizados</li>
              </ol>

              <h4>Consideraciones Importantes</h4>
              
              <h5>Seguridad:</h5>
              <ul>
                <li>Solo los Super Administradores pueden modificar roles y permisos</li>
                <li>No se puede modificar el rol del propio usuario actual</li>
                <li>Los permisos marcados como predeterminados no se pueden modificar directamente (deben cambiarse a través del rol)</li>
              </ul>

              <h5>Jerarquía de Permisos:</h5>
              <ul>
                <li>Los permisos se heredan del rol base</li>
                <li>Los permisos personalizados se suman a los del rol</li>
                <li>Al cambiar el rol de un usuario, se mantienen los permisos personalizados que no entren en conflicto</li>
              </ul>

              <h5>Indicadores Visuales:</h5>
              <ul>
                <li>Los checkboxes atenuados indican permisos heredados del rol</li>
                <li>Los checkboxes normales indican permisos personalizados</li>
                <li>Al pasar el cursor sobre un permiso, se muestra información sobre su origen</li>
              </ul>

              <h4>Mejores Prácticas</h4>
              <ul>
                <li><strong>Mínimos Privilegios:</strong> Asigna solo los permisos necesarios para cada usuario</li>
                <li><strong>Uso de Roles:</strong> Utiliza los roles predeterminados siempre que sea posible</li>
                <li><strong>Documentación:</strong> Mantén un registro de los cambios realizados en los permisos</li>
                <li><strong>Revisión Periódica:</strong> Revisa regularmente los permisos otorgados</li>
              </ul>

              <InfoBox>
                <h4><FaInfoCircle /> Soporte</h4>
                <p>Para problemas técnicos o preguntas sobre la gestión de permisos, contacta al equipo de soporte técnico con los siguientes detalles:</p>
                <ul>
                  <li>Nombre de usuario afectado</li>
                  <li>Rol actual</li>
                  <li>Permisos que debería tener</li>
                  <li>Comportamiento esperado vs. comportamiento actual</li>
                </ul>
              </InfoBox>
              
              <p><em>Esta documentación está diseñada para ayudarte a aprovechar al máximo el sistema de gestión de permisos. Si necesitas asistencia adicional, no dudes en contactar al equipo de soporte.</em></p>
            </div>
          </SectionContent>
        </DocumentationSection>
      </Card>
    </Container>
  );
};

export default PermissionsManager;
